/**
 * CercleVertueuxWidget — Widget Executive 360° "Mon cercle vertueux".
 *
 * Affiche en synthèse les 3 grands acteurs compatibles les plus prometteurs
 * de la dernière analyse Bras Armé. Si aucune analyse n'existe, propose un
 * CTA pour la lancer.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Crown, Coins, ChevronRight, Sparkles } from "lucide-react";
import BrasArmeAnalysisPanel from "@/components/BrasArmeAnalysisPanel";

export default function CercleVertueuxWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/bras-arme/cercle-vertueux?top=3");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData({ available: false, candidates: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [panelOpen]); // re-fetch when panel closes (in case user just generated)

  return (
    <div className="noam-card p-5" data-testid="cercle-vertueux-widget">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#EFD127]" />
          <div>
            <div className="noam-overline">{t("cercle_vertueux.overline", { defaultValue: "Stratégie · Super Partenaires" })}</div>
            <div className="noam-display text-base font-semibold">
              {t("cercle_vertueux.title", { defaultValue: "Mes Super Partenaires" })}
            </div>
          </div>
        </div>
        <button
          onClick={() => setPanelOpen(true)}
          className="btn-ghost text-xs"
          data-testid="cercle-vertueux-open-btn"
        >
          {data?.available ? (
            <>{t("cercle_vertueux.view_all", { defaultValue: "Voir l'analyse complète" })} <ChevronRight className="w-3 h-3" /></>
          ) : (
            <><Sparkles className="w-3 h-3" /> {t("cercle_vertueux.start", { defaultValue: "Lancer l'analyse" })}</>
          )}
        </button>
      </div>

      {loading && (
        <div className="text-xs text-zinc-500" data-testid="cercle-vertueux-loading">
          {t("cercle_vertueux.loading", { defaultValue: "Chargement…" })}
        </div>
      )}

      {!loading && !data?.available && (
        <div className="text-xs text-zinc-500 leading-relaxed" data-testid="cercle-vertueux-empty">
          {t("cercle_vertueux.empty", { defaultValue: "Aucune analyse encore. Découvrez les grandes institutions qui peuvent devenir vos Super Partenaires ou Super Clients." })}
        </div>
      )}

      {!loading && data?.available && data.candidates.length > 0 && (
        <div className="space-y-2" data-testid="cercle-vertueux-list">
          {data.candidates.map((c, i) => (
            <div
              key={i}
              className="flex items-start justify-between gap-3 p-2 rounded hover:bg-white/[0.02] transition cursor-pointer"
              onClick={() => setPanelOpen(true)}
              data-testid={`cercle-vertueux-item-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium text-zinc-200 truncate">{c.name}</span>
                  {c.also_capital_source && (
                    <Coins
                      className="w-3 h-3 text-[#EFD127]"
                      title={t("bras_arme.also_capital_hint", { defaultValue: "Aussi source de capital potentielle." })}
                    />
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{c.reason_needs_arm}</div>
              </div>
              <div className="text-right shrink-0">
                <div
                  className="text-base font-bold"
                  style={{ color: c.score_compatibility >= 75 ? "#1D9E75" : c.score_compatibility >= 50 ? "#EF9F27" : "#71717A" }}
                >
                  {c.score_compatibility}
                </div>
                <div className="text-[9px] uppercase tracking-wider text-zinc-600">
                  {t("cercle_vertueux.compat", { defaultValue: "compat." })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BrasArmeAnalysisPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        sourceModule="executive"
      />
    </div>
  );
}
